import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createSchemaApiIngestionSchemasPost,
  deleteSchemaApiIngestionSchemasSchemaIdDelete,
  getSchemaApiIngestionSchemasSchemaIdGet,
  listAllSchemasApiIngestionSchemasAllGet,
  listSchemasApiIngestionSchemasGet,
  patchSchemaApiIngestionSchemasSchemaIdPatch,
  type DocumentTypeSchema,
  type SchemaCreateRequest,
  type SchemaPatchRequest,
} from "@noa/api-core";
import { SchemaQueryKey } from "../../query-keys";
import { reportError } from "../../typed-client";
import { ApiError } from "../../errors";

export function useSchemaList() {
  const { data, isPending, refetch, isError, error } = useQuery({
    queryKey: SchemaQueryKey.list(),
    queryFn: async () => {
      // Fetch all schemas - admin UI typically has < 100 schemas
      // API max is 100; if more schemas exist, pagination would be needed
      const { data } = await listSchemasApiIngestionSchemasGet({
        query: { limit: 100 },
        throwOnError: true,
      });
      return data;
    },
  });

  return {
    schemaIds: data?.schema_ids ?? [],
    total: data?.total ?? 0,
    isLoading: isPending,
    refetch,
    isError,
    error,
  };
}

export function useAllSchemas() {
  // Single bulk request (GET /schemas/all, designed for admin UIs): replaces
  // the former list + N detail fetches, whose disabled-when-empty detail
  // query also kept isLoading true forever when no schema existed.
  const { data, isPending, refetch, isError, error } = useQuery({
    queryKey: [...SchemaQueryKey.all, "bulk"],
    queryFn: async () => {
      const { data } = await listAllSchemasApiIngestionSchemasAllGet({
        throwOnError: true,
      });
      return data;
    },
  });

  return {
    schemas: data?.schemas ?? [],
    isLoading: isPending,
    refetch,
    isError,
    error,
  };
}

export function useSchema(schemaId: string | null) {
  const { data, isPending, isError, error } = useQuery({
    queryKey: SchemaQueryKey.detail(schemaId ?? ""),
    queryFn: async () => {
      const { data } = await getSchemaApiIngestionSchemasSchemaIdGet({
        path: { schema_id: schemaId! },
        throwOnError: true,
      });
      return data;
    },
    enabled: !!schemaId,
  });

  return {
    schema: data,
    isLoading: isPending,
    isError,
    error,
  };
}

export function useCreateSchema() {
  const queryClient = useQueryClient();

  return useMutation<DocumentTypeSchema, ApiError, SchemaCreateRequest>({
    mutationFn: async (request) => {
      const { data } = await createSchemaApiIngestionSchemasPost({
        body: request,
        throwOnError: true,
      });
      return data;
    },
    onError: reportError,
    onSuccess: () => {
      // Invalidate the whole "schemas" prefix: the all-details query key
      // includes the id list, which does not change on update, so targeted
      // list/detail invalidation left the table showing stale data
      void queryClient.invalidateQueries({ queryKey: SchemaQueryKey.all });
    },
  });
}

export function useUpdateSchema(schemaId: string) {
  const queryClient = useQueryClient();

  return useMutation<DocumentTypeSchema, ApiError, SchemaPatchRequest>({
    mutationFn: async (request) => {
      const { data } = await patchSchemaApiIngestionSchemasSchemaIdPatch({
        path: { schema_id: schemaId },
        body: request,
        throwOnError: true,
      });
      return data;
    },
    onError: reportError,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SchemaQueryKey.all });
    },
  });
}

export function useDeleteSchema() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: async (schemaId) => {
      await deleteSchemaApiIngestionSchemasSchemaIdDelete({
        path: { schema_id: schemaId },
        throwOnError: true,
      });
    },
    onError: reportError,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SchemaQueryKey.all });
    },
  });
}
